import React, { useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { debounce } from 'lodash-es';

import SurveySchemaPartList from '../../SurveySchemaPartList';
import { withLang } from '../../../i18n';
import {
  publicationStatusValues,
  surveySchemaMetadataType,
  surveySchemaPartType,
  surveyTypeValues
} from '../../../constants/types';
import { EMPTY_PAGE } from '../../../constants';

import styles from './browseSurveyParts.module.css';
import useUrlSearch from '../../../hooks/useUrlSearch';
import useAsyncQuery from '../../../hooks/useAsyncQuery';
import { getExtraSurveyParts, getSurveySchemaParts } from '../../../api';
import SurveyPartSearch from '../SurveyPartSearch';

const PARSERS = {
  type: (sp) => sp.get('type') || surveyTypeValues.DEGREE_PROGRAM_SURVEY,
  keyword: (sp) => sp.get('keyword') || '',
  filters: (sp) => {
    const vals = sp.getAll('filters');
    return vals && vals.length ? vals : [];
  }
};

export const parseFromSearchParams = (sp, allowedKeys = []) => {
  const page = Number(sp.get('page') ?? 0) || 0;

  const criteria = allowedKeys.reduce((acc, key) => {
    const parser = PARSERS[key];
    if (parser) {
      acc[key] = parser(sp);
    }

    return acc;
  }, {});

  return { page, criteria };
};

const BrowseSurveyParts = ({
  isExtraPartsView,
  selectable,
  selectLimitOne,
  lockedType,
  defaultPublicationStatus,
  selectedParts,
  excludedParts,
  onSelectPart,
  translate: t
}) => {
  const {
    searchParams, page, setPage, setParams
  } = useUrlSearch();

  const allowedSearchKeys = useMemo(() => {
    const base = isExtraPartsView ? ['type', 'keyword', 'filters'] : ['type'];
    return lockedType ? base.filter((k) => k !== 'type') : base;
  }, [isExtraPartsView, lockedType]);

  const { criteria } = useMemo(
    () => parseFromSearchParams(searchParams, allowedSearchKeys),
    [searchParams, allowedSearchKeys]
  );

  const effectiveType = lockedType ?? criteria.type;

  const debouncedCriteriaChange = useMemo(() =>
    debounce(setParams, 800), [setParams]);

  useEffect(() =>
    () => debouncedCriteriaChange.cancel(), // clean-up on unmount
  [debouncedCriteriaChange]);

  const fetchPage = useCallback(() => (isExtraPartsView
    ? getExtraSurveyParts({
      type: effectiveType,
      page,
      status: defaultPublicationStatus,
      keyword: criteria.keyword,
      filters: criteria.filters
    })
    : getSurveySchemaParts({ type: effectiveType, page })),
  [isExtraPartsView, effectiveType, page, defaultPublicationStatus, criteria.keyword, criteria.filters]);

  const schemaPartPage = useAsyncQuery(fetchPage, { initialData: EMPTY_PAGE });

  const translationBase = isExtraPartsView ? 'extraSurveyParts' : 'surveySchemaParts';

  return (
    <div className={styles.browseContainer}>
      <h3>{t(`${translationBase}.browse`)}</h3>
      {allowedSearchKeys.length > 0 && (
        <SurveyPartSearch onChange={debouncedCriteriaChange} defaultCriteria={criteria} />
      )}
      <SurveySchemaPartList
        schemaPartPage={schemaPartPage}
        onPageChange={setPage}
        isExtraPartsView={isExtraPartsView}
        selectable={selectable}
        selectLimitOne={selectLimitOne}
        selectedParts={selectedParts}
        excludedParts={excludedParts}
        onSelectPart={onSelectPart}
        type={effectiveType}
      />
    </div>
  );
};

const {
  bool, func, oneOf, arrayOf
} = PropTypes;
BrowseSurveyParts.propTypes = {
  isExtraPartsView: bool,
  selectable: bool,
  selectLimitOne: bool,
  lockedType: oneOf(Object.values(surveyTypeValues)),
  defaultPublicationStatus: oneOf(Object.values(publicationStatusValues)),
  onSelectPart: func,
  selectedParts: arrayOf(surveySchemaMetadataType),
  excludedParts: arrayOf(surveySchemaPartType),
  translate: func.isRequired
};

BrowseSurveyParts.defaultProps = {
  isExtraPartsView: false,
  selectable: false,
  selectLimitOne: false,
  lockedType: undefined,
  defaultPublicationStatus: undefined,
  selectedParts: [],
  excludedParts: [],
  onSelectPart: () => ({})
};

export default withLang(BrowseSurveyParts);
